import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { Routes } from "@ctypes";
import { useBeneficiaryGiftingLink, useGoalsContext } from "@hooks";
import { Translations, i18n } from "@i18n";
import { ArtPanel } from "@routes/DashboardRoute/Home/styles";
import { home } from "@routes/DashboardRoute/Home/tokens";

const Band = styled(ArtPanel)`
  border-radius: 22px;
  display: flex;
  align-items: center;
  gap: 44px;
  padding: 30px 36px;
  background:
    radial-gradient(620px 420px at 8% -10%, rgba(233, 164, 186, 0.46), transparent 66%),
    radial-gradient(680px 460px at 88% 108%, rgba(224, 155, 124, 0.44), transparent 66%),
    radial-gradient(460px 380px at 52% 46%, rgba(126, 112, 168, 0.26), transparent 70%),
    linear-gradient(150deg, #2b2130 0%, #14121b 100%);

  @media (max-width: 1100px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 620px;
`;

const BandEyebrow = styled.div`
  display: flex;
  align-items: center;
  height: 26px;
  align-self: flex-start;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(14, 18, 26, 0.34);
  border: 1px solid ${home.line20};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${home.ink80};
`;

const BandTitle = styled.div`
  font-family: ${home.serif};
  font-size: 27px;
  line-height: 1.2;
  letter-spacing: -0.4px;
  text-wrap: pretty;

  em {
    font-style: italic;
  }
`;

const BandBody = styled.div`
  font-size: 15px;
  line-height: 1.55;
  color: ${home.ink66};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-left: auto;
  flex-shrink: 0;
  width: 300px;

  @media (max-width: 1100px) {
    margin-left: 0;
    width: 100%;
  }
`;

const LinkChip = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 46px;
  padding: 0 8px 0 16px;
  border-radius: 999px;
  background: rgba(14, 18, 26, 0.42);
  border: 1px solid rgba(242, 239, 233, 0.18);

  span {
    font-size: 13px;
    color: ${home.ink62};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const CopyButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 999px;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  flex-shrink: 0;
  cursor: pointer;
  color: ${home.ink80};

  &:hover {
    background: ${home.fill09};
  }
`;

const RequestButton = styled.button`
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  border: none;
  border-radius: 999px;
  background: ${home.ink};
  color: ${home.ground};
  font-family: ${home.sans};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    filter: brightness(1.08);
  }
`;

const Note = styled.div`
  font-size: 12px;
  text-align: center;
  color: ${home.ink46};
`;

export const GiftingBand = ({ name }: { name: string }) => {
  const navigate = useNavigate();
  const { beneficiaryId } = useGoalsContext();

  const [copied, setCopied] = useState(false);

  const { data } = useBeneficiaryGiftingLink({ beneficiaryId });

  const displayUrl = data?.giftingUrl?.replace(/^https?:\/\//, "");

  const handleCopy = () => {
    if (!data?.giftingUrl) return;

    navigator.clipboard.writeText(data.giftingUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ padding: `36px ${home.pagePad} 0` }}>
      <Band>
        <Copy>
          <BandEyebrow>{i18n.t(Translations.GoalsOverviewRouteGiftEyebrow, { name })}</BandEyebrow>
          <BandTitle>
            {i18n.t(Translations.GoalsOverviewRouteGiftTitleLead)}{" "}
            <em>{i18n.t(Translations.GoalsOverviewRouteGiftTitleHighlight)}</em>.
          </BandTitle>
          <BandBody>{i18n.t(Translations.GoalsOverviewRouteGiftBody, { name })}</BandBody>
        </Copy>

        <Actions>
          {displayUrl && (
            <LinkChip>
              <span>{displayUrl}</span>
              <CopyButton
                type="button"
                onClick={handleCopy}
                aria-label={i18n.t(Translations.RequestGiftRouteCopy)}
              >
                {copied ? (
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m4 12.5 5.5 5.5L20 6.5" />
                  </svg>
                ) : (
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="9" y="9" width="12" height="12" rx="2" />
                    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                  </svg>
                )}
              </CopyButton>
            </LinkChip>
          )}
          <RequestButton onClick={() => navigate(Routes.RequestGift, { state: { beneficiaryId } })}>
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="9" width="18" height="12" rx="1.5" />
              <path d="M3 13h18" />
              <path d="M12 9v12" />
              <path d="M12 9C10 9 7.5 8.6 7.5 6.5A2.5 2.5 0 0 1 12 5a2.5 2.5 0 0 1 4.5 1.5C16.5 8.6 14 9 12 9Z" />
            </svg>
            {i18n.t(Translations.RequestGiftRouteCTA)}
          </RequestButton>
          <Note>{i18n.t(Translations.GoalsOverviewRouteGiftNote)}</Note>
        </Actions>
      </Band>
    </div>
  );
};
